import type { LucideIcon } from "lucide-react";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import type { API } from "@upclaim/sdk";

import { Combobox } from "@/components/combobox";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { requestTypeVisual } from "./request-type-visual";

export type RequestTargetOption = {
  value: string;
  text: string;
  kind: "PASSENGER" | "LEGAL_REPRESENTATIVE" | "CLAIM";
};

/**
 * The "target" of a receipt for an expense the travel agency or the business travel company
 * advanced: those hang off the claim, not off anybody who flew, so there is no passenger to name
 * and the payload carries neither target FK (TECH-1035).
 *
 * A sentinel rather than an id, because the dossier is not a row anything points at — it is the
 * absence of a target, given a name so the jurist can pick it.
 */
export const CLAIM_EXPENSES_TARGET = "__claim_expenses__";

export type ExpenseOption = { id: number; label: string };

export type PassengerOption = {
  id: string;
  name: string;
  email: string | null;
  /** Declared expenses, so a receipt can be attached to the line it proves. */
  expenses: ExpenseOption[];
};

/**
 * Built from the SDK union, not from a hand-written list: adding a fifth requestable type on the
 * API side propagates here on the next `yarn sdk:ci` instead of silently drifting.
 */
type RequestableDocumentType = NonNullable<API.CreatePassengerRequestItem["claimDocumentType"]>;

type RequestKind = "DOCUMENT" | "EMAIL" | "LEGAL_REPRESENTATIVE" | "QUESTION";

/**
 * Une seule liste pour le genre de demande et la pièce : « Document » puis « Pièce d'identité »
 * était deux choix pour une seule décision. Chaque pièce y est directement, à côté des autres demandes.
 */
type RequestTypeKey = RequestableDocumentType | Exclude<RequestKind, "DOCUMENT">;

type RequestTypeOption = { key: RequestTypeKey; label: string; icon: LucideIcon; className: string };

const REQUEST_TYPES: RequestTypeOption[] = [
  { key: "ID_PROOF", label: "Pièce d'identité", ...requestTypeVisual("ID_PROOF") },
  { key: "ETICKET", label: "E-ticket", ...requestTypeVisual("ETICKET") },
  { key: "PROOF_OF_EXPENSE", label: "Justificatif de dépense", ...requestTypeVisual("PROOF_OF_EXPENSE") },
  { key: "OTHER", label: "Autre document", ...requestTypeVisual("OTHER") },
  { key: "EMAIL", label: "Adresse e-mail", ...requestTypeVisual("EMAIL") },
  {
    key: "LEGAL_REPRESENTATIVE",
    label: "Représentant légal",
    ...requestTypeVisual("LEGAL_REPRESENTATIVE"),
  },
  { key: "QUESTION", label: "Question", ...requestTypeVisual("QUESTION") },
];

function isDocumentType(key: RequestTypeKey): key is RequestableDocumentType {
  return key in REQUIRED_BY_DEFAULT;
}

function rowTypeKey(row: { kind: RequestKind; claimDocumentType: RequestableDocumentType }): RequestTypeKey {
  return row.kind === "DOCUMENT" ? row.claimDocumentType : row.kind;
}

function typePatch(key: RequestTypeKey): { kind: RequestKind; claimDocumentType?: RequestableDocumentType } {
  return isDocumentType(key) ? { kind: "DOCUMENT", claimDocumentType: key } : { kind: key };
}

function RequestTypeLabel({ option }: { option: RequestTypeOption }) {
  const Icon = option.icon;
  return (
    <>
      <Icon aria-hidden="true" className={cn("size-4 shrink-0", option.className)} />
      <span className="truncate">{option.label}</span>
    </>
  );
}

/**
 * Who a question is about. Unlike a pièce, "nobody in particular" is the common case — a question
 * about the flight or the booking concerns the whole dossier — so that is what a fresh row picks.
 */
const QUESTION_DOSSIER_TARGET = "";

/** Mirrors the API's cap on `description`, which is where the question is written. */
const QUESTION_MAX_LENGTH = 300;

/** Zero clicks for the common case: the pieces we cannot proceed without default to required. */
const REQUIRED_BY_DEFAULT: Record<RequestableDocumentType, boolean> = {
  ID_PROOF: true,
  ETICKET: true,
  PROOF_OF_EXPENSE: false,
  OTHER: true,
};

type DraftRow = {
  key: number;
  kind: RequestKind;
  claimDocumentType: RequestableDocumentType;
  target: string;
  expenseId: string;
  emailUserClaimId: string;
  /** A minor of the family whose legal representative we are asking for — the family is read off them. */
  legalRepresentativeUserClaimId: string;
  /** A passenger or representative of this family, or QUESTION_DOSSIER_TARGET for the whole dossier. */
  questionTarget: string;
  label: string;
  /** The optional précision — or, on a QUESTION, the question itself. */
  description: string;
  isRequired: boolean;
};

function newRow(
  key: number,
  targets: RequestTargetOption[],
  emailTargets: PassengerOption[],
  legalRepresentativeTargets: PassengerOption[],
): DraftRow {
  return {
    key,
    kind: "DOCUMENT",
    claimDocumentType: "ID_PROOF",
    target: targets[0]?.value ?? "",
    expenseId: "",
    emailUserClaimId: emailTargets[0]?.id ?? "",
    legalRepresentativeUserClaimId: legalRepresentativeTargets[0]?.id ?? "",
    questionTarget: QUESTION_DOSSIER_TARGET,
    label: "",
    description: "",
    isRequired: true,
  };
}

/** The address already on file is the context that was missing: we usually re-ask because it is wrong. */
function passengerEmailLabel(passenger: PassengerOption): string {
  return passenger.email ? `${passenger.name} · ${passenger.email}` : `${passenger.name} · sans adresse`;
}

/**
 * Two lines where `<option>` could only hold one string: the name carries the row, the address is
 * the secondary detail the jurist checks before asking for a new one.
 */
function PassengerRow({ passenger }: { passenger: PassengerOption }) {
  return (
    <span className="flex min-w-0 flex-col">
      <span className="truncate">{passenger.name}</span>
      <span className="truncate text-xs text-muted-foreground">{passenger.email ?? "sans adresse"}</span>
    </span>
  );
}

export function CreatePassengerRequestsDialog({
  open,
  onOpenChange,
  passengers,
  emailTargets,
  legalRepresentativeTargets,
  legalRepresentative,
  claimExpenses,
  onSubmit,
  pending = false,
}: {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Who a DOCUMENT can be filed against: this acquisition's family, and their expenses. */
  passengers: PassengerOption[];
  /**
   * Who an EMAIL can be asked about: every passenger of the CLAIM we cannot reach, not just this
   * family. The passengers whose address is missing are exactly the ones in families nobody ever
   * wrote to — they have no acquisition of their own, so a family-scoped list is always empty for
   * them, which is why this is passed in rather than derived from `passengers`.
   */
  emailTargets: PassengerOption[];
  /**
   * Families with no adult and no legal representative on file, represented by one of their minors. Only they
   * can be asked for a legal representative — anyone else either has an adult who can sign, or a
   * legal representative already.
   */
  legalRepresentativeTargets: PassengerOption[];
  legalRepresentative: { id: string; name: string } | null;
  /**
   * Expenses carried by the claim itself — advanced by the travel agency or the business travel
   * company. Nobody declared them, so they are reachable only through the dossier pseudo-target.
   */
  claimExpenses: ExpenseOption[];
  onSubmit: (requests: API.CreatePassengerRequestItem[]) => void;
  pending?: boolean;
}) {
  const passengerTargets: RequestTargetOption[] = [
    ...passengers.map((passenger) => ({
      value: passenger.id,
      text: passenger.name,
      kind: "PASSENGER" as const,
    })),
    // Only for acquisitions that actually have one.
    ...(legalRepresentative
      ? [
          {
            value: legalRepresentative.id,
            text: `Représentant légal · ${legalRepresentative.name}`,
            kind: "LEGAL_REPRESENTATIVE" as const,
          },
        ]
      : []),
  ];

  /**
   * The dossier joins the list only for a receipt, and only when there is something to justify.
   *
   * On any other pièce it would be meaningless — an identity document belongs to a person — and
   * the API rejects an untargeted one anyway.
   */
  const claimTarget: RequestTargetOption = {
    value: CLAIM_EXPENSES_TARGET,
    text: "Dépenses liées au dossier",
    kind: "CLAIM",
  };
  const targetsFor = (claimDocumentType: RequestableDocumentType): RequestTargetOption[] =>
    claimDocumentType === "PROOF_OF_EXPENSE" && claimExpenses.length > 0
      ? [...passengerTargets, claimTarget]
      : passengerTargets;

  // What a fresh row and a reset fall back to: a real person, never the dossier.
  const targets = passengerTargets;

  // « Représentant légal » n'existe que si une famille en a besoin.
  const rowTypes = REQUEST_TYPES.filter(
    (option) => option.key !== "LEGAL_REPRESENTATIVE" || legalRepresentativeTargets.length > 0,
  );

  const [seq, setSeq] = useState(2);
  const [rows, setRows] = useState<DraftRow[]>([
    newRow(1, targets, emailTargets, legalRepresentativeTargets),
  ]);

  function patch(key: number, changes: Partial<DraftRow>) {
    setRows((current) =>
      current.map((row) => {
        if (row.key !== key) {
          return row;
        }

        const next = { ...row, ...changes };

        // An EMAIL or LEGAL_REPRESENTATIVE ask is about somebody this family may not even know,
        // so it never blocks them and never triggers relaunches — it cannot be made mandatory.
        if (next.kind === "EMAIL" || next.kind === "LEGAL_REPRESENTATIVE") {
          next.isRequired = false;
        }
        // A question is asked of this family: optional by default, the jurist can raise it.
        if (changes.kind === "QUESTION" && row.kind !== "QUESTION") {
          next.isRequired = false;
        }
        // Back to a pièce: follow its type's default again.
        if (changes.kind === "DOCUMENT" && row.kind !== "DOCUMENT") {
          next.isRequired = REQUIRED_BY_DEFAULT[next.claimDocumentType];
        }
        // "Aucun" exists only for OTHER, so leaving OTHER must re-pick a real target.
        if (next.claimDocumentType !== "OTHER" && next.target === "") {
          next.target = targets[0]?.value ?? "";
        }
        // The dossier exists only for a receipt: leaving PROOF_OF_EXPENSE with it still selected
        // would send an untargeted ID_PROOF, which the API rejects.
        if (next.claimDocumentType !== "PROOF_OF_EXPENSE" && next.target === CLAIM_EXPENSES_TARGET) {
          next.target = targets[0]?.value ?? "";
        }
        // Follow the type's default until the jurist overrides it themselves.
        if (changes.claimDocumentType && changes.isRequired === undefined) {
          next.isRequired = REQUIRED_BY_DEFAULT[changes.claimDocumentType];
        }
        // An expense only means anything on a receipt, and only for whoever it hangs off — the
        // passenger who declared it, or the dossier. Changing either has to re-pick.
        if (changes.claimDocumentType || changes.target) {
          const available = expensesFor(next.target);
          next.expenseId =
            next.claimDocumentType === "PROOF_OF_EXPENSE" && available[0] ? String(available[0].id) : "";
        }

        return next;
      }),
    );
  }

  function toPayload(): API.CreatePassengerRequestItem[] {
    return rows.map((row) => {
      if (row.kind === "EMAIL") {
        return {
          type: "EMAIL",
          userClaimId: row.emailUserClaimId,
          description: row.description || undefined,
          isRequired: false,
        };
      }

      if (row.kind === "LEGAL_REPRESENTATIVE") {
        return {
          type: "LEGAL_REPRESENTATIVE",
          userClaimId: row.legalRepresentativeUserClaimId,
          description: row.description || undefined,
          isRequired: false,
        };
      }

      if (row.kind === "QUESTION") {
        const questionTarget = passengerTargets.find((option) => option.value === row.questionTarget);

        return {
          type: "QUESTION",
          description: row.description.trim(),
          userClaimId: questionTarget?.kind === "PASSENGER" ? questionTarget.value : undefined,
          legalRepresentativeId:
            questionTarget?.kind === "LEGAL_REPRESENTATIVE" ? questionTarget.value : undefined,
          isRequired: row.isRequired,
        };
      }

      const target = targetsFor(row.claimDocumentType).find((option) => option.value === row.target);

      return {
        type: "DOCUMENT",
        claimDocumentType: row.claimDocumentType,
        expenseId: row.expenseId ? Number(row.expenseId) : undefined,
        userClaimId: target?.kind === "PASSENGER" ? target.value : undefined,
        legalRepresentativeId: target?.kind === "LEGAL_REPRESENTATIVE" ? target.value : undefined,
        label: row.label || undefined,
        description: row.description || undefined,
        isRequired: row.isRequired,
      };
    });
  }

  function expensesFor(target: string): ExpenseOption[] {
    if (target === CLAIM_EXPENSES_TARGET) {
      return claimExpenses;
    }

    return passengers.find((passenger) => passenger.id === target)?.expenses ?? [];
  }

  const requiredCount = rows.filter((row) => row.isRequired).length;

  // A receipt with no expense, or a question with no wording, would be rejected by the API, so do
  // not let it be submitted. The index is kept, not just the fact: at three rows, "which one?" is
  // the jurist's next question.
  const blockedIndex = rows.findIndex(
    (row) =>
      (row.kind === "DOCUMENT" && row.claimDocumentType === "PROOF_OF_EXPENSE" && !row.expenseId) ||
      (row.kind === "QUESTION" && !row.description.trim()),
  );
  const incomplete = blockedIndex !== -1;
  const blockedReason =
    rows[blockedIndex]?.kind === "QUESTION" ? "une question sans intitulé" : "un justificatif sans dépense";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Demander des pièces au passager</DialogTitle>
          <DialogDescription>
            Les demandes sont enregistrées sans envoi. L&apos;e-mail part avec « Envoyer la demande ».
          </DialogDescription>
        </DialogHeader>

        <DialogPanel className="flex flex-col gap-3">
          {rows.map((row, index) => {
            const isOther = row.claimDocumentType === "OTHER";
            const targetOptional = row.kind === "DOCUMENT" && isOther;
            const targetLabel = targetOptional ? "Pour (facultatif)" : "Pour *";
            const rowTargets = targetsFor(row.claimDocumentType);
            const rowExpenses = expensesFor(row.target);

            return (
              <Card key={row.key} className="gap-3 p-3.5">
                <div className="flex items-center gap-2.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Demande {index + 1}
                  </span>
                  {rows.length > 1 ? (
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      className="ml-auto"
                      aria-label="Retirer cette demande"
                      onClick={() => setRows((current) => current.filter((r) => r.key !== row.key))}
                    >
                      <Trash2 />
                    </Button>
                  ) : null}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field>
                    <FieldLabel>Demande</FieldLabel>
                    <Combobox<RequestTypeOption>
                      ariaLabel="Demande"
                      items={rowTypes}
                      value={rowTypes.find((option) => option.key === rowTypeKey(row)) ?? null}
                      // Re-clicking the selected row would clear it: a request always has a type.
                      onChange={(option) => option && patch(row.key, typePatch(option.key))}
                      getKey={(option) => option.key}
                      getLabel={(option) => option.label}
                      renderItem={(option) => <RequestTypeLabel option={option} />}
                      renderValue={(option) => <RequestTypeLabel option={option} />}
                      searchPlaceholder="Rechercher..."
                      emptyMessage="Aucun type"
                    />
                  </Field>

                  {row.kind === "QUESTION" ? (
                    <Field>
                      <FieldLabel>Pour</FieldLabel>
                      <Combobox<RequestTargetOption>
                        ariaLabel="Pour"
                        items={passengerTargets}
                        value={passengerTargets.find((option) => option.value === row.questionTarget) ?? null}
                        onChange={(option) =>
                          patch(row.key, { questionTarget: option?.value ?? QUESTION_DOSSIER_TARGET })
                        }
                        getKey={(option) => option.value}
                        getLabel={(option) => option.text}
                        unselectLabel="Tout le dossier"
                        placeholder="Tout le dossier"
                        searchPlaceholder="Rechercher..."
                        emptyMessage="Aucun passager"
                      />
                    </Field>
                  ) : row.kind === "DOCUMENT" ? (
                    <Field>
                      <FieldLabel>{targetLabel}</FieldLabel>
                      <Combobox<RequestTargetOption>
                        ariaLabel={targetLabel}
                        items={rowTargets}
                        value={rowTargets.find((option) => option.value === row.target) ?? null}
                        onChange={(option) => {
                          // Only OTHER can end up with no target; anywhere else, re-clicking the
                          // selected row must not empty a field the API requires.
                          if (option || targetOptional) {
                            patch(row.key, { target: option?.value ?? "" });
                          }
                        }}
                        getKey={(option) => option.value}
                        getLabel={(option) => option.text}
                        unselectLabel={targetOptional ? "Personne — pièce du dossier" : undefined}
                        placeholder="Personne — pièce du dossier"
                        searchPlaceholder="Rechercher..."
                        emptyMessage="Aucun destinataire"
                      />
                    </Field>
                  ) : row.kind === "LEGAL_REPRESENTATIVE" ? (
                    <Field>
                      <FieldLabel>Pour la famille</FieldLabel>
                      <Combobox<PassengerOption>
                        ariaLabel="Pour la famille"
                        items={legalRepresentativeTargets}
                        value={
                          legalRepresentativeTargets.find(
                            (passenger) => passenger.id === row.legalRepresentativeUserClaimId,
                          ) ?? null
                        }
                        onChange={(passenger) => {
                          if (passenger) {
                            patch(row.key, { legalRepresentativeUserClaimId: passenger.id });
                          }
                        }}
                        getKey={(passenger) => passenger.id}
                        getLabel={(passenger) => passenger.name}
                        placeholder="Aucune famille"
                        searchPlaceholder="Rechercher une famille..."
                        emptyMessage="Aucune famille à représenter"
                      />
                    </Field>
                  ) : (
                    <Field>
                      <FieldLabel>Pour le passager</FieldLabel>
                      <Combobox<PassengerOption>
                        ariaLabel="Pour le passager"
                        items={emailTargets}
                        value={
                          emailTargets.find((passenger) => passenger.id === row.emailUserClaimId) ?? null
                        }
                        onChange={(passenger) => {
                          if (passenger) {
                            patch(row.key, { emailUserClaimId: passenger.id });
                          }
                        }}
                        getKey={(passenger) => passenger.id}
                        getLabel={passengerEmailLabel}
                        renderItem={(passenger) => <PassengerRow passenger={passenger} />}
                        placeholder="Aucun passager"
                        searchPlaceholder="Rechercher un passager..."
                        emptyMessage="Aucun passager à contacter"
                      />
                    </Field>
                  )}
                </div>

                {row.kind === "DOCUMENT" && row.target === CLAIM_EXPENSES_TARGET ? (
                  <p className="text-xs text-muted-foreground">
                    Frais avancés par l&apos;agence ou l&apos;entreprise : le justificatif est rattaché au
                    dossier, pas à un passager.
                  </p>
                ) : null}

                {row.kind === "DOCUMENT" && row.claimDocumentType === "PROOF_OF_EXPENSE" ? (
                  <Field>
                    <FieldLabel>Dépense *</FieldLabel>
                    {/* `match` is forced: this is not a ValidityState failure — nothing has been
                        typed — but a row with nothing to point at, which still blocks the save. */}
                    {rowExpenses.length === 0 ? (
                      <FieldError match>
                        Ce passager n&apos;a déclaré aucune dépense — un justificatif ne peut pas être demandé
                        tant qu&apos;il n&apos;y en a pas.
                        {claimExpenses.length > 0
                          ? " Pour des frais avancés par l'agence ou l'entreprise, choisissez « Dépenses liées au dossier »."
                          : null}
                      </FieldError>
                    ) : (
                      <Combobox<ExpenseOption>
                        ariaLabel="Dépense *"
                        items={rowExpenses}
                        value={rowExpenses.find((expense) => String(expense.id) === row.expenseId) ?? null}
                        onChange={(expense) => {
                          if (expense) {
                            patch(row.key, { expenseId: String(expense.id) });
                          }
                        }}
                        getKey={(expense) => expense.id}
                        getLabel={(expense) => expense.label}
                        placeholder="Choisir une dépense"
                        searchPlaceholder="Rechercher une dépense..."
                        emptyMessage="Aucune dépense"
                      />
                    )}
                  </Field>
                ) : null}

                {row.kind === "QUESTION" ? (
                  <Field>
                    <FieldLabel>Question posée au passager *</FieldLabel>
                    <Textarea
                      size="sm"
                      required
                      maxLength={QUESTION_MAX_LENGTH}
                      placeholder="À quelle heure êtes-vous finalement arrivés à destination ?"
                      value={row.description}
                      onChange={(event) => patch(row.key, { description: event.target.value })}
                    />
                    <FieldDescription>
                      Le passager y répond par écrit depuis sa page de suivi ; sa réponse s&apos;affiche ici.
                    </FieldDescription>
                  </Field>
                ) : null}

                {row.kind === "DOCUMENT" && isOther ? (
                  <Field>
                    <FieldLabel>Nom affiché au passager *</FieldLabel>
                    <Input
                      required
                      placeholder="Attestation d'hébergement"
                      value={row.label}
                      onChange={(event) => patch(row.key, { label: event.target.value })}
                    />
                  </Field>
                ) : null}

                {/* A question's wording already lives in the description field above. */}
                {row.kind === "QUESTION" ? null : (
                  <Field>
                    <FieldLabel>Précision (facultatif)</FieldLabel>
                    <Textarea
                      size="sm"
                      placeholder={
                        row.kind === "DOCUMENT"
                          ? "Ce qui aide le passager à trouver la bonne pièce"
                          : "Le contexte qui aide le passager à répondre"
                      }
                      value={row.description}
                      onChange={(event) => patch(row.key, { description: event.target.value })}
                    />
                  </Field>
                )}

                {/* Un interrupteur, pas un choix à deux boutons : la demande est obligatoire ou ne l'est
                    pas. Une adresse ou un représentant légal ne l'est jamais — la raison s'affiche à la place. */}
                {row.kind === "DOCUMENT" || row.kind === "QUESTION" ? (
                  <div className="flex items-center gap-3 text-sm">
                    <Switch
                      aria-label="Obligatoire"
                      checked={row.isRequired}
                      onCheckedChange={(isRequired: boolean) => patch(row.key, { isRequired })}
                    />
                    <span className="font-medium text-foreground">Obligatoire</span>
                    <span className="text-muted-foreground">
                      {row.isRequired
                        ? "Relances automatiques tant qu'elle manque"
                        : "Si disponible, sans relance"}
                    </span>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {row.kind === "EMAIL"
                      ? "Toujours facultative : elle ne bloque pas ce passager et ne déclenche aucune relance."
                      : "Toujours facultative : elle porte sur une autre famille et ne déclenche aucune relance."}
                  </p>
                )}
              </Card>
            );
          })}

          <Button
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => {
              setRows((current) => [
                ...current,
                newRow(seq, targets, emailTargets, legalRepresentativeTargets),
              ]);
              setSeq((current) => current + 1);
            }}
          >
            <Plus />
            Ajouter une demande
          </Button>
        </DialogPanel>

        <DialogFooter className="items-center">
          {/* A disabled save with no stated reason is a dead end: in a multi-row dialog the row
              that blocks it is regularly scrolled out of sight. Say what is missing. */}
          <span
            className={cn(
              "mr-auto text-xs",
              incomplete ? "text-destructive-foreground" : "text-muted-foreground",
            )}
          >
            {incomplete
              ? `Demande ${blockedIndex + 1} : ${blockedReason}`
              : `${requiredCount} obligatoire${requiredCount > 1 ? "s" : ""} sur ${rows.length}`}
          </span>
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>
            Annuler
          </Button>
          <Button loading={pending} disabled={incomplete} onClick={() => onSubmit(toPayload())}>
            {rows.length > 1 ? `Enregistrer ${rows.length} demandes` : "Enregistrer la demande"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
